import _ from 'lodash'
import store from '../store'
import { isContextChange } from './routeHelpers'
import { setScreenContext, updateWindow } from 'actions/interactableActions'
import StandardModalContent from 'GeneralUI/StandardModalContent/StandardModalContent.jsx'
import React from 'react'
import * as modalActions from 'actions/modalActions'
import { sendWindowMessage } from '../actions/windowMessagingActions'
import { allWindows } from '../data/systemWindows'

/**
 * Extract a formatted full name from a patient object
 * @param {Object} patient
 * @param {boolean} includeMi include middle initial, if present
 * @param {boolean} lastFirst format name by last, first instead of first last
 * @returns {String}
 */
export const getPatientFullName = (
  patient,
  includeMi = false,
  lastFirst = false
) => {
  if (!_.isObject(patient)) {
    return ''
  }
  let firstmid = patient.firstname || ''
  if (includeMi && _.isString(patient.mi) && patient.mi.length > 0) {
    firstmid += ' ' + patient.mi
  }
  if (lastFirst) {
    return `${patient.lastname}, ${firstmid}`
  } else {
    return `${firstmid} ${patient.lastname}`
  }
}

/**
 * Returns the path fpr a given patient
 * @param {number} patientId
 */
export const getPatientPath = patientId => `/dashboard/patient/${patientId}`

/**
 * Opens patient context fpr a given patient
 * @param {number} patientId
 */
export const openPatient = patientId => {
  // Based on PatientSearch.jsx, this seemed like the best approach
  // for moving into patient context
  const patientPath = getPatientPath(patientId)
  if (window.location.pathname.toLowerCase() !== patientPath) {
    store.dispatch(
      setScreenContext(
        patientPath
      )
    )
  }
}

/**
 * Returns true if the patient context for given patient is already open
 * @param {number} patientId
 */
export const isPatientOpen = patientId => {
  const patientPath = getPatientPath(patientId)
  return window.location.pathname.toLowerCase() === patientPath
}
/**
 * Opens relevant patient context and SkinTestDetailWindow for given skintest
 * @param {number} patientId
 * @param {number} skintestId
 */
export const openSkinTest = (patientId, skintestId) => {
  const patientPath = getPatientPath(patientId)
  const message = {
    type: 'LOAD_DATA',
    payload: skintestId
  }
  openWindow(
    'skinTestDetail',
    patientPath,
    message
  )
}

/**
 * Opens relevant patient context and VialTestScoringWindow
 * @param {number} patientId
 */
export const openVialTests = patientId => {
  const patientPath = getPatientPath(patientId)
  openWindow(
    'vialTestScoring',
    patientPath
  )
}

/**
 * Returns the mix window name to use based on configured windows.
 * If SSM (singleScreenMix) is configured, use it; otherwise fall back to vialMixing.
 */
export const getMixWindowName = () => {
  const windows = store.getState()?.appConfig?.patientDisplay?.windows || []
  const configuredWindows = Array.isArray(windows) ? windows : Object.values(windows)
  const hasSingleScreenMix = configuredWindows.some(w => {
    return w.ref === 'singleScreenMix' || w.id === 'singleScreenMix'
  })

  return hasSingleScreenMix ? 'singleScreenMix' : 'vialMixing'
}

const getWindowConfig = (windowName, route) => {
  const {patientDisplay = {}, adminDisplay = {}, nurseDashboard = {}} = _.cloneDeep(store.getState()?.appConfig || {})

  let displayInfo
  if(route.indexOf('patient') !== -1) {
    displayInfo = patientDisplay
  } else if (route.indexOf('dashboard/admin') !== -1) {
    displayInfo = adminDisplay
  } else if (route.indexOf('dashboard') !== -1)  {
    displayInfo = nurseDashboard
  }
  
  const { windows = {} } = displayInfo

  const config = windows.find(win => {
    return win.ref === windowName
  })
  if(config) {
    // we found a window configuration
    return config
  } else {
    // see if the window has a default configuration we can use
    const sysConfig = allWindows.find(windowConfig => windowConfig.id === windowName)
    return sysConfig?.defaultWindowConfig == null ? false : {ref: windowName, ...sysConfig.defaultWindowConfig}
  }
}

export const gotoWindow = (
  windowName,
  route,
  messages,
  previousPage
) => {
  if (!Array.isArray(messages)) {
    messages = [messages]
  }

  const messagesContainer = messages.map(message => {
    return {
      recipient: windowName,
      message
    }
  })

  if(isContextChange(route)) {
    store.dispatch(setScreenContext(route, getWindowConfig(windowName, route), messagesContainer))
    return true
  } else {
    // find the window config for the window from appConfig
    const openWindows = _.cloneDeep(store.getState()?.interactableState?.windows || {})
    if(openWindows[windowName]) {
      // the window is already open in the current context so just send the messages.
      messages.forEach(message => {
        store.dispatch(sendWindowMessage(
          windowName,
          message)
        )
      })
      return true
    } else {
      const windowConfig = getWindowConfig(windowName, route);
      // the window isn't open so we need to find its default config and open it
      if (windowConfig) {
        // add our URL to the props so the destination page can return here if needed
        windowConfig.previousPage = previousPage

        // now load the patient path and include the id of the
        // treatment set that the window will be looking for in the url (this.props.match).

        store.dispatch(updateWindow(windowConfig))
        // Dispatch messages separately to ensure delivery even if the window's
        // message queue was already initialized (e.g. by SET_VIEW)
        messages.forEach(message => {
          store.dispatch(sendWindowMessage(windowName, message))
        })
        return true
      }
    }
  }
  return false
}

// TODO: this and gotoWindow REALLY need to be converted to sagas so we don't have to pass
// appConfig in and they can properly read state and dispatch actions without using the store.
export const openWindow = (
  windowName,
  route,
  messages = [],
  previousPage = null
) => {
  if (!Array.isArray(messages)) {
    messages = [messages]
  }

  messages.push({
    type: 'BRING_TO_FRONT',
    payload: {}
  })

  const windowsToTry = Array.isArray(windowName) ? windowName : [windowName]

  let ok = false
  for (const name of windowsToTry) {
    ok = gotoWindow(name, route, [...messages], previousPage)
    if (ok) {
      break
    }
  }

  if (!ok) {
    const displayName = windowsToTry.join("' or '")
    store.dispatch(
      modalActions.openModal('gotoWindow_error', {
        title: 'Window Not Found',
        content: (
          <StandardModalContent
            closeSelf={() => {
              store.dispatch(modalActions.closeModal('gotoWindow_error'))
            }}
            messageContent={
              <span>
                The '{displayName}' window is not configured in Settings. Please
                configure it and retry.
              </span>
            }
            okHandler={() => {}}
            okText={'OK'}
          />
        ),
        closeable: true
      })
    )
  }
}

export const DEFAULT_PATIENT_BOX_COLOR="#A9A9A9"